import * as THREE from 'three';
import { FikoGrup } from '../FikoGrup';
import { FikoSahne } from '../FikoSahne';
import { FikoDinamikKesitInteraction } from './FikoDinamikKesitInteraction';
import { FikoSecililer } from '../FikoSecililer';
import { FikoDinamikKesitShader } from './FikoDinamikKesitShader';

/**
 * Fiko Dinamik Kesit: CATIA Tarzı Profesyonel Dynamic Sectioning
 *
 * Özellikler:
 * - Gerçek zamanlı kesit düzlemi manipülasyonu
 * - Çoklu kesit düzlemi desteği (6 düzlem'e kadar)
 * - Smooth/Flip kesit modu
 * - Kesit dolgu ve çizgi görselleştirmesi
 * - Kesit düzlemi gizatörü (manipulator)
 * - Performance optimized clipping planes
 * - WASM-accelerated geometry calculations (10-50x faster)
 * - Export kesit geometrisi
 */
export class FikoDinamikKesit extends FikoGrup {
    override type: any = "FikoDinamikKesit";

    // GPU-based clipping via native THREE.js clipping planes
    private injectedMaterials: Set<THREE.Material> = new Set();

    // Section fill groups (stencil-based kesit yüzeyi)
    private sectionFillGroups: Map<THREE.Plane, THREE.Group> = new Map();

    // Kesit Düzlemleri
    kesitDuzlemleri: THREE.Plane[] = [];
    aktifDuzlemIndex: number = 0;
    maksimumDuzlem: number = 6;

    // Kesit Ayarları
    ayarlar: FikoDinamikKesitAyarlar = {
        kesitModu: 'smooth', // 'smooth' | 'flip'
        kesitDolguRenk: 0xff6b6b,
        kesitDolguOpacity: 0.6,
        kesitCizgiRenk: 0xff0000,
        kesitCizgiKalinlik: 3,
        gosterKesitDuzlemi: true,
        gosterKesitDolgu: false, // Cap (kapak) render - kesilen yüzey doldurma
        gosterKesitCizgi: true,
        duzlemBoyutu: 1000,
        duzlemOpacity: 0.15
    };

    // Manipülatörler (Gizatörler)
    manipulatorGrup: THREE.Group;
    aktifManipulator: FikoKesitManipulator | null = null;
    manipulatorler: FikoKesitManipulator[] = [];

    // State
    aktif: boolean = false;
    public hedefMeshler: THREE.Mesh[] = [];
    private hedefObjeler: THREE.Object3D[] = []; // Tüm render edilebilir objeler (Lines, Points, vb.)

    // Mouse Interaction
    private interactionHandler: FikoDinamikKesitInteraction | null = null;
    public mouseKontrolAktif: boolean = true;

    // Yüzey Seçimi
    public yuzeySecimAktif: boolean = false;
    private fikoSecimYuzey: FikoSecililer | null = null;

    // Kesit Geometrileri (Fill ve Outline)
    kesitGeometrileri: THREE.Group;
    private kesitDolgulari: THREE.Mesh[] = [];
    private kesitCizgileri: THREE.LineSegments[] = [];

    // 🎯 GÖRÜNÜRLÜK KONTROL: Toggle edilebilir elemanlar
    public halkaGorunur: boolean = true;        // Döndürme halkaları
    public gridGorunur: boolean = true;         // Grid taşıma düzlemi
    public kesitCizgisiGorunur: boolean = true; // Kesit boundary çizgileri
    public okGorunur: boolean = true;           // Taşıma okları
    public eksenGorunur: boolean = true;        // Eksen yardımcısı (AxesHelper)

    constructor(public override sahne?: FikoSahne, name?: string) {
        super(sahne, name);

        this.name = name || 'Dinamik Kesit';
        this.renderOrder = 1000;
        this.userData.FikoArayuz.agactaGorunur = true;
        this.userData.FikoArayuz.listeGoster = true;
        this.userData.FikoArayuz.simge = "./simgeler/kesit.svg";

        // 🎯 Kaydedilmiş ayarları yükle
        this.ayarlariYukle();

        // Manipülatör grubu
        this.manipulatorGrup = new THREE.Group();
        this.manipulatorGrup.name = 'Kesit Manipülatörler';
        this.add(this.manipulatorGrup);

        // Kesit geometrileri grubu
        this.kesitGeometrileri = new THREE.Group();
        this.kesitGeometrileri.name = 'Kesit Geometrileri';
        this.add(this.kesitGeometrileri);

        // İlk kesit düzlemini oluştur
        this.yeniKesitDuzlemiEkle();
    }


    /**
     * Yeni kesit düzlemi ekle
     */
    public yeniKesitDuzlemiEkle(normal?: THREE.Vector3, constant?: number): number {
        if (this.kesitDuzlemleri.length >= this.maksimumDuzlem) {
            return -1;
        }

        const plane = new THREE.Plane(
            normal || new THREE.Vector3(0, 0, 1),
            constant || 0
        );

        const index = this.kesitDuzlemleri.push(plane) - 1;

        // Manipülatör oluştur
        const manipulator = new FikoKesitManipulator(
            plane,
            this.ayarlar,
            index,
            this
        );
        this.manipulatorler.push(manipulator);
        this.manipulatorGrup.add(manipulator);

        // 🎯 Yeni manipulatöre görünürlük ayarlarını uygula
        this.manipulatorGorunurlukUygula(manipulator);

        // Yeni düzlemi aktif yap
        this.aktifDuzlemDegistir(index);

        // Kesit aktifse section fill oluştur
        if (this.aktif && (this.ayarlar.gosterKesitDolgu || this.ayarlar.gosterKesitCizgi)) {
            // 🎯 Görünürlük flag'ini doğrudan shader'a ilet (flicker önleme)
            const group = FikoDinamikKesitShader.createSectionFill(
                plane,
                this.hedefMeshler,
                this.ayarlar.kesitDolguRenk,
                this.ayarlar.kesitDolguOpacity,
                this.ayarlar.kesitCizgiRenk,
                this.ayarlar.kesitCizgiKalinlik,
                this.kesitCizgisiGorunur, // 🎯 Çizgi görünürlüğü
                this.ayarlar.gosterKesitDolgu // 🎯 Cap (kapak) görünürlüğü
            );

            this.kesitGeometrileri.add(group);
            this.sectionFillGroups.set(plane, group);
            this.analitikKesitProfiliCiz(plane, group);   // CATIA: GERÇEK B-Rep kesit profili (analitik, mesh-cap yanına)
        }

        return index;
    }

    /** CATIA Dynamic Section: düzlemle GERÇEK B-Rep kesit profilini (BRepAlgoAPI_Section) analitik çizgi olarak çizer. B-Rep yoksa sessiz geçer (mesh-cap kalır). */
    private analitikKesitProfiliCiz(plane: THREE.Plane, group: THREE.Group): void {
        const ana: any = this.sahne?.anaGosterim;
        if (!ana?.brepKesitProfili) return;
        const n = plane.normal;
        const origin = { x: n.x * -plane.constant, y: n.y * -plane.constant, z: n.z * -plane.constant };
        let prof: any[] | null = null;
        try { prof = ana.brepKesitProfili(origin, { x: n.x, y: n.y, z: n.z }); } catch { }
        if (!prof || !prof.length) return;
        const pts: number[] = [];
        for (const e of prof) { const ep = e?.pts; if (!ep || ep.length < 2) continue; for (let i = 1; i < ep.length; i++) { pts.push(ep[i - 1].x, ep[i - 1].y, ep[i - 1].z, ep[i].x, ep[i].y, ep[i].z); } }
        if (!pts.length) return;
        const geo = new THREE.BufferGeometry(); geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
        const mat = new THREE.LineBasicMaterial({ color: (this.ayarlar.kesitCizgiRenk as any) ?? 0x00e5ff, depthTest: false, transparent: true, linewidth: 2 });
        const ls = new THREE.LineSegments(geo, mat); ls.renderOrder = 996; ls.name = 'brep_kesit_profili'; ls.userData['isSectionLine'] = true; ls.userData['brepProfil'] = true;
        group.add(ls);
    }

    /**
     * Kesit düzlemini sil
     */
    public kesitDuzlemiSil(index: number): void {
        if (index < 0 || index >= this.kesitDuzlemleri.length) {
            return;
        }

        // Section fill'i temizle (splice'dan önce plane'i al)
        const plane = this.kesitDuzlemleri[index];
        const sectionGroup = this.sectionFillGroups.get(plane);
        if (sectionGroup) {
            FikoDinamikKesitShader.removeSectionFill(plane);
            this.kesitGeometrileri.remove(sectionGroup);
            this.sectionFillGroups.delete(plane);
        }

        this.kesitDuzlemleri.splice(index, 1);

        const manipulator = this.manipulatorler[index];
        if (manipulator) {
            this.manipulatorGrup.remove(manipulator);
            manipulator.dispose();
            this.manipulatorler.splice(index, 1);
        }

        if (this.aktifDuzlemIndex >= this.kesitDuzlemleri.length) {
            this.aktifDuzlemIndex = Math.max(0, this.kesitDuzlemleri.length - 1);
        }

        this.aktifManipulator = this.manipulatorler[this.aktifDuzlemIndex] || null;

        this.clippingPlanleriGuncelle();
    }

    /**
     * Aktif kesit düzlemini değiştir
     */
    public aktifDuzlemDegistir(index: number): void {
        if (index < 0 || index >= this.kesitDuzlemleri.length) {
            return;
        }

        this.aktifDuzlemIndex = index;
        this.aktifManipulator = this.manipulatorler[index];

        // Tüm manipülatörleri inactive yap
        this.manipulatorler.forEach((m, i) => {
            m.setAktif(i === index);
        });
    }

    /**
     * Kesiti başlat - CATIA-tarzı GPU-based native clipping
     * THREE.js native clipping planes kullanır
     * %100 GPU-based, her frame otomatik güncellenir
     */
    public kesitiBaslat(hedefMeshler?: THREE.Mesh[]): void {
        if (this.aktif) {
            return;
        }

        // Hedef meshleri belirle
        if (hedefMeshler && hedefMeshler.length > 0) {
            this.hedefMeshler = hedefMeshler;
        } else {
            this.hedefMeshler = this.sahneMeshleriniAl();
        }

        if (this.hedefMeshler.length === 0) {
            return;
        }

        // Tüm render edilebilir objeleri topla
        this.hedefObjeler = this.tumRenderObjeleriniAl();

        // ===== RENDERER'DA LOCAL CLIPPING AKTİF ET (KRİTİK!) =====
        if (this.sahne?.anaGosterim?.sunucu) {
            this.sahne.anaGosterim.sunucu.localClippingEnabled = true;
        }

        const totalMeshes = this.hedefMeshler.length;

        // Her mesh'in material'ine clipping planes ekle (reference - auto-update)
        this.hedefMeshler.forEach(mesh => {
            if (mesh.material) {
                const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];

                materials.forEach((mat: THREE.Material) => {
                    if (!this.injectedMaterials.has(mat)) {
                        FikoDinamikKesitShader.applyClipping(mat, this.kesitDuzlemleri);
                        this.injectedMaterials.add(mat);
                    }
                });
            }
        });

        // Diğer objelere de clipping ekle (Lines, Points vb.)
        this.hedefObjeler.forEach(obj => {
            const objWithMaterial = obj as any;
            if (objWithMaterial.material) {
                const materials = Array.isArray(objWithMaterial.material) ? objWithMaterial.material : [objWithMaterial.material];

                materials.forEach((mat: THREE.Material) => {
                    if (!this.injectedMaterials.has(mat)) {
                        FikoDinamikKesitShader.applyClipping(mat, this.kesitDuzlemleri);
                        this.injectedMaterials.add(mat);
                    }
                });
            }
        });

        this.aktif = true;

        // ===== SECTION FILL OLUŞTUR (Stencil-based) =====
        if (this.ayarlar.gosterKesitDolgu || this.ayarlar.gosterKesitCizgi) {
            this.sectionFillOlustur();
        }

        // Mouse interaction başlat
        if (this.mouseKontrolAktif) {
            this.mouseInteractionBaslat();
        }

        // 🎯 Kaydedilmiş görünürlük durumlarını uygula
        this.gorunurlukDurumlariniUygula();
    }

    /**
     * Section fill ve çizgileri oluştur (stencil-based)
     */
    private sectionFillOlustur(): void {
        // Her kesit düzlemi için section fill oluştur
        this.kesitDuzlemleri.forEach(plane => {
            if (this.sectionFillGroups.has(plane)) {
                return; // Zaten var
            }

            // 🎯 Görünürlük flag'ini doğrudan shader'a ilet (flicker önleme)
            const group = FikoDinamikKesitShader.createSectionFill(
                plane,
                this.hedefMeshler,
                this.ayarlar.kesitDolguRenk,
                this.ayarlar.kesitDolguOpacity,
                this.ayarlar.kesitCizgiRenk,
                this.ayarlar.kesitCizgiKalinlik,
                this.kesitCizgisiGorunur, // 🎯 Çizgi görünürlüğü
                this.ayarlar.gosterKesitDolgu // 🎯 Cap (kapak) görünürlüğü
            );

            this.kesitGeometrileri.add(group);
            this.sectionFillGroups.set(plane, group);
        });
    }

    /**
     * Kesiti durdur - clipping planes'i kaldır
     */
    public kesitiDurdur(): void {
        if (!this.aktif) {
            return;
        }

        // ===== REMOVE CLIPPING PLANES FROM ALL MATERIALS =====
        this.injectedMaterials.forEach(mat => {
            FikoDinamikKesitShader.removeClipping(mat);
        });

        // ===== SECTION FILL'LERİ TEMİZLE =====
        this.sectionFillGroups.forEach((group, plane) => {
            FikoDinamikKesitShader.removeSectionFill(plane);
            this.kesitGeometrileri.remove(group);
        });
        this.sectionFillGroups.clear();

        this.injectedMaterials.clear();
        this.hedefMeshler = [];
        this.hedefObjeler = [];
        this.aktif = false;

        // Mouse interaction durdur
        this.mouseInteractionDurdur();

        // Kesit geometrilerini temizle
        this.kesitGeometrileriniTemizle();
    }

    /**
     * Clipping planes güncelle
     * Native THREE.js clipping - plane referansları değiştiğinde otomatik güncellenir
     */
    private clippingPlanleriGuncelle(): void {
        if (!this.aktif || this.hedefMeshler.length === 0) {
            return;
        }

        // Native clipping planes array reference olarak kullanılıyor
        // Plane.normal ve Plane.constant değiştiğinde GPU otomatik günceller

        // ===== SECTION FILL GÜNCELLE =====
        if (this.ayarlar.gosterKesitDolgu || this.ayarlar.gosterKesitCizgi) {
            this.sectionFillGroups.forEach((group, plane) => {
                // 🎯 Görünürlük flag'ini doğrudan shader'a ilet (flicker önleme)
                FikoDinamikKesitShader.updateSectionFill(
                    plane,
                    this.hedefMeshler,
                    group,
                    this.ayarlar.kesitCizgiRenk,
                    this.ayarlar.kesitCizgiKalinlik,
                    this.kesitCizgisiGorunur, // 🎯 Çizgi görünürlüğü
                    this.ayarlar.gosterKesitDolgu, // 🎯 Cap (kapak) görünürlüğü
                    this.ayarlar.kesitDolguRenk,
                    this.ayarlar.kesitDolguOpacity
                );
            });
        }

        // Kesit görselleştirmesini güncelle (manipulator plane'i)
        if (this.ayarlar.gosterKesitDuzlemi) {
            this.kesitGeometrileriniGuncelle();
        }

        // CATIA Dynamic Section: düzlem hareket ederken GERÇEK B-Rep profilini canlı yeniden hesapla (throttle'lı — Section pahalı)
        if (this.ayarlar.gosterKesitCizgi) {
            this.brepProfilCanliGuncelle();
        }

        // 🎯 Güncelleme sonrası tüm görünürlük ayarlarını yeniden uygula
        this.gorunurlukDurumlariniUygula();
    }

    private _brepProfilSonZaman = 0;
    private _brepProfilTimer: any = null;
    /** Düzlem sürüklenirken B-Rep kesit profilini throttle'lı yeniden çizer (drag sonu kesin hesap garantili). B-Rep yoksa hiç çalışmaz (mesh-cap kalır). */
    private brepProfilCanliGuncelle(): void {
        const ana: any = this.sahne?.anaGosterim;
        if (!ana?.brepKesitProfili) return;   // B-Rep yok → mesh-cap zaten var, uğraşma
        const calistir = () => {
            this._brepProfilSonZaman = performance.now();
            this.sectionFillGroups.forEach((group, plane) => {
                const eski = group.children.filter((c: any) => c.userData?.['brepProfil']);
                for (const e of eski) { group.remove(e); (e as any).geometry?.dispose?.(); (e as any).material?.dispose?.(); }
                this.analitikKesitProfiliCiz(plane, group);
            });
        };
        const simdi = performance.now();
        if (simdi - this._brepProfilSonZaman > 100) { clearTimeout(this._brepProfilTimer); calistir(); }
        else { clearTimeout(this._brepProfilTimer); this._brepProfilTimer = setTimeout(calistir, 110); }   // trailing: drag bitince kesin hesap
    }

    /**
     * Tüm görünürlük durumlarını mevcut flag'lere göre uygula
     * Güncelleme sonrası çağrılır
     */
    private gorunurlukDurumlariniUygula(): void {
        // Tüm manipulatörlere görünürlük uygula
        this.manipulatorler.forEach(m => this.manipulatorGorunurlukUygula(m));

        // Kesit çizgileri ve cap görünürlüğü
        this.sectionFillGroups.forEach((group) => {
            group.traverse((child: any) => {
                // Kesit çizgileri
                if (child.name === 'section_line' || child.userData?.isSectionLine) {
                    child.visible = this.kesitCizgisiGorunur;
                    // 🎯 Raycast'i de kapat/aç (seçim çakışmasını önle)
                    if (!this.kesitCizgisiGorunur) {
                        if (!child._originalRaycast) {
                            child._originalRaycast = child.raycast;
                        }
                        child.raycast = () => {};
                    } else if (child._originalRaycast) {
                        child.raycast = child._originalRaycast;
                    }
                }
                // Cap (kapak) mesh - contour triangulation ve cap group dahil
                if (child.name === 'section_cap' ||
                    child.name === 'section_cap_group' ||
                    child.name === 'stencil_cap_group' ||
                    child.name === 'cap_contour_group' ||
                    child.name === 'cap_triangulation_group' ||
                    child.name === 'cap_backface_group' ||
                    child.name.startsWith('section_cap') ||
                    child.userData?.isSectionCap ||
                    child.userData?.isSectionCapBackface) {
                    child.visible = this.ayarlar.gosterKesitDolgu;
                }
            });
        });
        this.kesitCizgileri.forEach((line: any) => {
            line.visible = this.kesitCizgisiGorunur;
            // 🎯 Raycast'i de kapat/aç
            if (!this.kesitCizgisiGorunur) {
                if (!line._originalRaycast) {
                    line._originalRaycast = line.raycast;
                }
                line.raycast = () => {};
            } else if (line._originalRaycast) {
                line.raycast = line._originalRaycast;
            }
        });
    }

    /**
     * Tek bir manipulatöre görünürlük ayarlarını uygula
     */
    private manipulatorGorunurlukUygula(m: FikoKesitManipulator): void {
        // Halkalar
        if (m.dondurmeHalkalar) {
            m.dondurmeHalkalar.visible = this.halkaGorunur;
            m.dondurmeHalkalar.traverse((child: any) => {
                if (child.isMesh || child.isLine) {
                    if (!this.halkaGorunur) {
                        if (!child._originalRaycast) {
                            child._originalRaycast = child.raycast;
                        }
                        child.raycast = () => {};
                    } else if (child._originalRaycast) {
                        child.raycast = child._originalRaycast;
                    }
                }
            });
        }

        // Grid ve düzlem mesh
        if (m.gridHelper) {
            m.gridHelper.visible = this.gridGorunur;
        }
        if (m.duzlemMesh) {
            m.duzlemMesh.visible = this.gridGorunur;
            if (!this.gridGorunur) {
                if (!m.duzlemMesh.userData._originalRaycast) {
                    m.duzlemMesh.userData._originalRaycast = m.duzlemMesh.raycast.bind(m.duzlemMesh);
                }
                m.duzlemMesh.raycast = () => {};
            } else if (m.duzlemMesh.userData._originalRaycast) {
                m.duzlemMesh.raycast = m.duzlemMesh.userData._originalRaycast;
            }
        }

        // Oklar
        if (m.oklar) {
            m.oklar.visible = this.okGorunur;
            m.oklar.traverse((child: any) => {
                if (child.isMesh || child.isLine) {
                    if (!this.okGorunur) {
                        if (!child._originalRaycast) {
                            child._originalRaycast = child.raycast;
                        }
                        child.raycast = () => {};
                    } else if (child._originalRaycast) {
                        child.raycast = child._originalRaycast;
                    }
                }
            });
        }

        // Eksen
        if (m.eksenHelper) {
            m.eksenHelper.visible = this.eksenGorunur;
            if (!this.eksenGorunur) {
                if (!m.eksenHelper.userData._originalRaycast) {
                    m.eksenHelper.userData._originalRaycast = m.eksenHelper.raycast.bind(m.eksenHelper);
                }
                m.eksenHelper.raycast = () => {};
            } else if (m.eksenHelper.userData._originalRaycast) {
                m.eksenHelper.raycast = m.eksenHelper.userData._originalRaycast;
            }
        }
    }

    /**
     * Sahnedeki tüm meshleri al
     */
    private sahneMeshleriniAl(): THREE.Mesh[] {
        const meshler: THREE.Mesh[] = [];

        if (this.sahne?.anaGosterim?.sahneler?.[0]) {
            this.sahne.anaGosterim.sahneler[0].traverse((child: any) => {
                if (child instanceof THREE.Mesh &&
                    child.visible &&
                    child.name !== 'Ölçümler' &&
                    child.userData.type !== 'FikoOlcumler' &&
                    !child.userData.kesitManipulator &&
                    !child.userData.excludeFromClipping) {
                    meshler.push(child);
                }
            });
        }

        return meshler;
    }

    /**
     * Sahnedeki tüm render edilebilir objeleri al (Lines, Points, LineSegments vb.)
     */
    private tumRenderObjeleriniAl(): THREE.Object3D[] {
        const objeler: THREE.Object3D[] = [];

        if (this.sahne?.anaGosterim?.sahneler?.[0]) {
            this.sahne.anaGosterim.sahneler[0].traverse((child: any) => {
                // LineSegments, Line, Points gibi render edilebilir objeler
                if ((child instanceof THREE.LineSegments ||
                     child instanceof THREE.Line ||
                     child instanceof THREE.Points ||
                     child instanceof THREE.LineLoop) &&
                    child.visible &&
                    child.name !== 'Ölçümler' &&
                    child.userData.type !== 'FikoOlcumler' &&
                    !child.userData.kesitManipulator &&
                    !child.userData.isPlaneMesh &&
                    !child.userData.excludeFromClipping) { // Similasyon çizgisi, takım vb. dahil etme
                    objeler.push(child);
                }
            });
        }

        return objeler;
    }

    /**
     * Aktif düzlemi hareket ettir
     */
    public duzlemHareketEttir(offset: number): void {
        if (!this.aktifManipulator) {
            return;
        }

        const plane = this.kesitDuzlemleri[this.aktifDuzlemIndex];
        plane.constant += offset;

        this.aktifManipulator.guncelle();
        this.clippingPlanleriGuncelle();

        if (this.sahne?.anaGosterim) {
            this.sahne.anaGosterim.ciz();
        }
    }

    /**
     * Aktif düzlemi döndür
     */
    public duzlemDondur(axis: THREE.Vector3, angle: number): void {
        if (!this.aktifManipulator) {
            return;
        }

        const plane = this.kesitDuzlemleri[this.aktifDuzlemIndex];
        plane.normal.applyAxisAngle(axis, angle);
        plane.normal.normalize();

        this.aktifManipulator.guncelle();
        this.clippingPlanleriGuncelle();

        if (this.sahne?.anaGosterim) {
            this.sahne.anaGosterim.ciz();
        }
    }

    /**
     * Düzlemi preset eksenine ayarla
     */
    public duzlemEkseneAyarla(eksen: 'x' | 'y' | 'z' | '-x' | '-y' | '-z'): void {
        if (!this.aktifManipulator) {
            return;
        }

        const plane = this.kesitDuzlemleri[this.aktifDuzlemIndex];

        switch (eksen) {
            case 'x':
                plane.normal.set(1, 0, 0);
                break;
            case '-x':
                plane.normal.set(-1, 0, 0);
                break;
            case 'y':
                plane.normal.set(0, 1, 0);
                break;
            case '-y':
                plane.normal.set(0, -1, 0);
                break;
            case 'z':
                plane.normal.set(0, 0, 1);
                break;
            case '-z':
                plane.normal.set(0, 0, -1);
                break;
        }

        this.aktifManipulator.guncelle();
        this.clippingPlanleriGuncelle();

        if (this.sahne?.anaGosterim) {
            this.sahne.anaGosterim.ciz();
        }
    }

    /**
     * Düzlem normalini ters çevir (inverse/flip)
     */
    public duzlemInverse(): void {
        if (!this.aktifManipulator) {
            return;
        }

        const plane = this.kesitDuzlemleri[this.aktifDuzlemIndex];

        // Normal'i ters çevir
        plane.normal.negate();
        plane.constant = -plane.constant;

        this.aktifManipulator.guncelle();
        this.clippingPlanleriGuncelle();

        if (this.sahne?.anaGosterim) {
            this.sahne.anaGosterim.ciz();
        }
    }

    /**
     * Yüzey seçimine göre kesit düzlemi ayarla
     */
    public yuzeySecimBaslat(): void {
        if (!this.sahne?.anaGosterim) {
            return;
        }

        const seciliMeshler = this.hedefMeshler.length > 0
            ? this.hedefMeshler
            : this.sahneMeshleriniAl();

        if (seciliMeshler.length === 0) {
            return;
        }

        if (this.fikoSecimYuzey) {
            this.fikoSecimYuzey.bitir();
            this.fikoSecimYuzey = null;
        }

        this.fikoSecimYuzey = new FikoSecililer(this.sahne.anaGosterim);
        this.fikoSecimYuzey.rayObje = seciliMeshler;
        this.fikoSecimYuzey.filitreler = ["Mesh"];
        this.fikoSecimYuzey.raycaster.params.Mesh = { threshold: 0 };

        this.fikoSecimYuzey.tiklandi.subscribe((kesisim: any) => {
            let faceNormal: THREE.Vector3 | null = null;
            let facePoint: THREE.Vector3 | null = null;
            const actualMesh = kesisim?.altObje || kesisim?.object;
            const mesh = actualMesh as THREE.Mesh;

            // Tıklanan noktayı al
            if (kesisim?.point) {
                facePoint = kesisim.point.clone();
            }

            // Face normal'ini al
            if (kesisim?.face?.normal) {
                const localNormal = kesisim.face.normal.clone();
                faceNormal = localNormal.applyMatrix3(
                    new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld)
                ).normalize();
            } else if (mesh.geometry && kesisim.faceIndex !== undefined) {
                const geometry = mesh.geometry as THREE.BufferGeometry;
                const normalAttr = geometry.getAttribute('normal') as THREE.BufferAttribute;
                if (normalAttr) {
                    const faceIndex = kesisim.faceIndex;
                    const normalArray = new Float32Array(3);
                    normalArray[0] = normalAttr.getX(faceIndex);
                    normalArray[1] = normalAttr.getY(faceIndex);
                    normalArray[2] = normalAttr.getZ(faceIndex);
                    const localNormal = new THREE.Vector3(normalArray[0], normalArray[1], normalArray[2]);
                    faceNormal = localNormal.applyMatrix3(
                        new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld)
                    ).normalize();
                }
            }

            if (faceNormal && facePoint && this.aktifManipulator) {
                // Aktif düzlemin normal'ini ve constant'ını güncelle
                const plane = this.kesitDuzlemleri[this.aktifDuzlemIndex];
                plane.normal.copy(faceNormal);

                // Plane constant'ı hesapla (düzlem noktadan geçecek şekilde)
                plane.constant = -faceNormal.dot(facePoint);

                // Manipülatörü güncelle
                this.aktifManipulator.guncelle();
                this.clippingPlanleriGuncelle();

                if (this.sahne?.anaGosterim) {
                    this.sahne.anaGosterim.ciz();
                }

                this.yuzeySecimDurdur();
            }
        });

        this.fikoSecimYuzey.baslat();
        this.fikoSecimYuzey.aktif = true;
        this.yuzeySecimAktif = true;
    }

    /**
     * Yüzey seçimini durdur
     */
    public yuzeySecimDurdur(): void {
        if (this.fikoSecimYuzey) {
            this.fikoSecimYuzey.aktif = false;
            this.fikoSecimYuzey.bitir();
            this.fikoSecimYuzey = null;
        }
        this.yuzeySecimAktif = false;
    }

    /**
     * Düzlemi seçili yüzeyin merkezine taşı
     */
    public duzlemYuzeyMerkezineTasi(): void {
        if (!this.sahne?.anaGosterim) {
            return;
        }

        if (!this.aktifManipulator) {
            return;
        }

        const seciliMeshler = this.hedefMeshler.length > 0
            ? this.hedefMeshler
            : this.sahneMeshleriniAl();

        if (seciliMeshler.length === 0) {
            return;
        }

        // Yüzey seçimi başlat
        if (this.fikoSecimYuzey) {
            this.fikoSecimYuzey.bitir();
            this.fikoSecimYuzey = null;
        }

        this.fikoSecimYuzey = new FikoSecililer(this.sahne.anaGosterim);
        this.fikoSecimYuzey.rayObje = seciliMeshler;
        this.fikoSecimYuzey.filitreler = ["Mesh"];
        this.fikoSecimYuzey.raycaster.params.Mesh = { threshold: 0 };

        // Seçim event'ini dinle
        this.fikoSecimYuzey.tiklandi.subscribe((kesisim: any) => {
            const actualMesh = kesisim?.altObje || kesisim?.object;
            const mesh = actualMesh as THREE.Mesh;

            if (!mesh || !kesisim?.face) {
                return;
            }

            const geometry = mesh.geometry as THREE.BufferGeometry;
            const positions = geometry.attributes.position;
            const face = kesisim.face;

            // Face'in üç köşesini al
            const v0 = new THREE.Vector3().fromBufferAttribute(positions, face.a);
            const v1 = new THREE.Vector3().fromBufferAttribute(positions, face.b);
            const v2 = new THREE.Vector3().fromBufferAttribute(positions, face.c);

            // World space'e çevir
            v0.applyMatrix4(mesh.matrixWorld);
            v1.applyMatrix4(mesh.matrixWorld);
            v2.applyMatrix4(mesh.matrixWorld);

            // Üçgenin merkez noktası
            const center = new THREE.Vector3()
                .add(v0)
                .add(v1)
                .add(v2)
                .divideScalar(3);

            // Normal vektörü hesapla
            const normal = new THREE.Vector3();
            normal.crossVectors(
                v1.clone().sub(v0),
                v2.clone().sub(v0)
            ).normalize();

            // Plane'i güncelle - Merkez noktasından geçecek şekilde
            const plane = this.kesitDuzlemleri[this.aktifDuzlemIndex];
            plane.normal.copy(normal);
            plane.constant = -plane.normal.dot(center);

            // Manipulator'ı güncelle
            this.aktifManipulator.guncelle();
            this.clippingPlanleriGuncelle();

            // Grid'i yüzey merkezine taşı ve boyutlandır
            this.aktifManipulator.dinamikBoyutAyarla();

            // Yüzey seçimini durdur
            this.yuzeySecimDurdur();

            if (this.sahne?.anaGosterim) {
                this.sahne.anaGosterim.ciz();
            }
        });

        this.fikoSecimYuzey.baslat();
        this.fikoSecimYuzey.aktif = true;
        this.yuzeySecimAktif = true;
    }

    /**
     * Düzlemi sahne merkezine taşı
     */
    public duzlemSahneMerkezineTasi(): void {
        if (!this.aktifManipulator) {
            return;
        }

        const seciliMeshler = this.hedefMeshler.length > 0
            ? this.hedefMeshler
            : this.sahneMeshleriniAl();

        if (seciliMeshler.length === 0) {
            return;
        }

        // Tüm meshlerin bounding box'ını hesapla
        const bbox = new THREE.Box3();
        seciliMeshler.forEach(mesh => {
            const meshBox = new THREE.Box3().setFromObject(mesh);
            bbox.union(meshBox);
        });

        // Merkez noktasını al
        const center = new THREE.Vector3();
        bbox.getCenter(center);

        // Plane'i güncelle - Normal yönünü koru, sadece pozisyonu değiştir
        const plane = this.kesitDuzlemleri[this.aktifDuzlemIndex];
        plane.constant = -plane.normal.dot(center);

        // Manipulator'ı güncelle
        this.aktifManipulator.guncelle();
        this.clippingPlanleriGuncelle();

        if (this.sahne?.anaGosterim) {
            this.sahne.anaGosterim.ciz();
        }
    }

    /**
     * Kesit geometrilerini güncelle
     * SIMPLIFIED: Shader tabanlı sistem CPU-side geometrileri oluşturmaz
     * Sadece manipulator plane'i görselleştirmek için kullanılır
     */
    private kesitGeometrileriniGuncelle(): void {
        // Eski geometrileri temizle
        this.kesitGeometrileriniTemizle();

        // Shader'da section lines ve fill oluşturuluyor
        // CPU side'da sadece manipulator plane'i göster
        if (this.aktifManipulator) {
            // Bir sonraki frame'de grid'i güncelle
            setTimeout(() => {
                if (this.aktifManipulator) {
                    this.aktifManipulator.dinamikBoyutAyarla();
                }
            }, 50);
        }
    }

    /**
     * ⚠️ DEPRECATED: CPU-side intersection calculation removed
     * All clipping and section visualization is now done in fragment shaders
     * This function is no longer used and has been removed for performance
     */

    /**
     * Kesit geometrilerini temizle
     */
    private kesitGeometrileriniTemizle(): void {
        this.kesitDolgulari.forEach(mesh => {
            if (mesh.geometry) {
                mesh.geometry.dispose();
            }
            if (mesh.material) {
                (mesh.material as THREE.Material).dispose();
            }
            this.kesitGeometrileri.remove(mesh);
        });

        this.kesitCizgileri.forEach(line => {
            if (line.geometry) {
                line.geometry.dispose();
            }
            if (line.material) {
                (line.material as THREE.Material).dispose();
            }
            this.kesitGeometrileri.remove(line);
        });

        // Thick line meshes'leri temizle
        const thickLineMeshes: THREE.Object3D[] = [];
        this.kesitGeometrileri.traverse(child => {
            if (child.name === 'kesit_line_mesh') {
                thickLineMeshes.push(child);
            }
        });

        thickLineMeshes.forEach(mesh => {
            if ((mesh as THREE.Mesh).geometry) {
                (mesh as THREE.Mesh).geometry.dispose();
            }
            if ((mesh as THREE.Mesh).material) {
                const mat = (mesh as THREE.Mesh).material;
                if (Array.isArray(mat)) {
                    mat.forEach(m => m.dispose());
                } else {
                    mat.dispose();
                }
            }
            this.kesitGeometrileri.remove(mesh);
        });

        this.kesitDolgulari = [];
        this.kesitCizgileri = [];
    }

    // ═══════════════════════════════════════════════════════════════════════
    // 🎯 GÖRÜNÜRLÜK KONTROL METODLARİ
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Döndürme halkalarının görünürlüğünü toggle et
     */
    public halkaGorunurlukToggle(): void {
        this.halkaGorunur = !this.halkaGorunur;
        this.manipulatorler.forEach(m => {
            if (m.dondurmeHalkalar) {
                m.dondurmeHalkalar.visible = this.halkaGorunur;
                // 🎯 Raycast'i de kapat/aç
                m.dondurmeHalkalar.traverse((child: any) => {
                    if (child.isMesh || child.isLine) {
                        child.raycast = this.halkaGorunur ? child._originalRaycast || THREE.Mesh.prototype.raycast : () => {};
                        if (this.halkaGorunur && !child._originalRaycast) {
                            child._originalRaycast = child.raycast;
                        }
                    }
                });
            }
        });
        this.sahne?.anaGosterim?.ciz();
        this.ayarlariKaydet();
    }

    /**
     * Grid taşıma düzleminin görünürlüğünü toggle et
     */
    public gridGorunurlukToggle(): void {
        this.gridGorunur = !this.gridGorunur;
        this.manipulatorler.forEach(m => {
            if (m.gridHelper) {
                m.gridHelper.visible = this.gridGorunur;
            }
            // Düzlem mesh'ini de kontrol et (grid'in üzerindeki şeffaf yüzey)
            if (m.duzlemMesh) {
                m.duzlemMesh.visible = this.gridGorunur;
                // 🎯 Raycast'i de kapat/aç
                if (!this.gridGorunur) {
                    if (!m.duzlemMesh.userData._originalRaycast) {
                        m.duzlemMesh.userData._originalRaycast = m.duzlemMesh.raycast.bind(m.duzlemMesh);
                    }
                    m.duzlemMesh.raycast = () => {};
                } else if (m.duzlemMesh.userData._originalRaycast) {
                    m.duzlemMesh.raycast = m.duzlemMesh.userData._originalRaycast;
                }
            }
        });
        this.sahne?.anaGosterim?.ciz();
        this.ayarlariKaydet();
    }

    /**
     * Kesit boundary çizgilerinin görünürlüğünü toggle et
     */
    public kesitCizgisiGorunurlukToggle(): void {
        this.kesitCizgisiGorunur = !this.kesitCizgisiGorunur;

        // Section fill gruplarındaki çizgileri kontrol et
        this.sectionFillGroups.forEach((group) => {
            group.traverse((child: any) => {
                if (child.name === 'section_line' || child.userData?.isSectionLine) {
                    child.visible = this.kesitCizgisiGorunur;
                    // 🎯 Raycast'i de kapat/aç (seçim çakışmasını önle)
                    if (!this.kesitCizgisiGorunur) {
                        if (!child._originalRaycast) {
                            child._originalRaycast = child.raycast;
                        }
                        child.raycast = () => {};
                    } else if (child._originalRaycast) {
                        child.raycast = child._originalRaycast;
                    }
                }
            });
        });

        // kesitCizgileri array'indeki çizgileri de kontrol et
        this.kesitCizgileri.forEach((line: any) => {
            line.visible = this.kesitCizgisiGorunur;
            // 🎯 Raycast'i de kapat/aç
            if (!this.kesitCizgisiGorunur) {
                if (!line._originalRaycast) {
                    line._originalRaycast = line.raycast;
                }
                line.raycast = () => {};
            } else if (line._originalRaycast) {
                line.raycast = line._originalRaycast;
            }
        });

        this.sahne?.anaGosterim?.ciz();
        this.ayarlariKaydet();
    }

    /**
     * Aktif kesit konturunu 2D teknik resim olarak dışa aktarır (DXF veya SVG).
     * Kontur çizgileri aktif düzleme projekte edilir → CAM/teknik çizim aktarımı.
     */
    public konturDisariAktar(format: 'dxf' | 'svg' = 'dxf'): boolean {
        const plane = this.kesitDuzlemleri[this.aktifDuzlemIndex];
        if (!plane || !this.kesitGeometrileri) return false;

        // Düzlem üzerinde 2D taban (u, v) kur
        const normal = plane.normal.clone().normalize();
        const origin = normal.clone().multiplyScalar(-plane.constant);
        const yardim = Math.abs(normal.y) < 0.99 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
        const u = new THREE.Vector3().crossVectors(yardim, normal).normalize();
        const v = new THREE.Vector3().crossVectors(normal, u).normalize();

        // Tüm kontur LineSegments'lerden dünya-uzayı segmentlerini topla, 2D'ye projekte et
        const segmentler: { x1: number; y1: number; x2: number; y2: number }[] = [];
        const p = new THREE.Vector3();
        const rel = new THREE.Vector3();
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        const projekte = (vx: number, vy: number, vz: number): [number, number] => {
            p.set(vx, vy, vz);
            rel.subVectors(p, origin);
            const x = rel.dot(u);
            const y = rel.dot(v);
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
            return [x, y];
        };

        this.kesitGeometrileri.updateMatrixWorld(true);
        this.kesitGeometrileri.traverse((child: any) => {
            if (!(child instanceof THREE.LineSegments) || child.visible === false) return;
            const pos = child.geometry?.getAttribute?.('position');
            if (!pos) return;
            const mw = child.matrixWorld;
            for (let i = 0; i + 1 < pos.count; i += 2) {
                const a = p.set(pos.getX(i), pos.getY(i), pos.getZ(i)).applyMatrix4(mw).clone();
                const b = p.set(pos.getX(i + 1), pos.getY(i + 1), pos.getZ(i + 1)).applyMatrix4(mw).clone();
                const [x1, y1] = projekte(a.x, a.y, a.z);
                const [x2, y2] = projekte(b.x, b.y, b.z);
                segmentler.push({ x1, y1, x2, y2 });
            }
        });

        if (!segmentler.length) return false;

        const icerik = format === 'dxf' ? this.dxfUret(segmentler) : this.svgUret(segmentler, minX, minY, maxX, maxY);
        const mime = format === 'dxf' ? 'application/dxf' : 'image/svg+xml';
        const blob = new Blob([icerik], { type: `${mime};charset=utf-8` });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `kesit-kontur-${Date.now()}.${format}`;
        a.click();
        URL.revokeObjectURL(url);
        return true;
    }

    private dxfUret(segmentler: { x1: number; y1: number; x2: number; y2: number }[]): string {
        const s: string[] = ['0', 'SECTION', '2', 'ENTITIES'];
        for (const seg of segmentler) {
            s.push('0', 'LINE', '8', 'KESIT',
                '10', seg.x1.toFixed(4), '20', seg.y1.toFixed(4), '30', '0.0',
                '11', seg.x2.toFixed(4), '21', seg.y2.toFixed(4), '31', '0.0');
        }
        s.push('0', 'ENDSEC', '0', 'EOF');
        return s.join('\r\n');
    }

    private svgUret(segmentler: { x1: number; y1: number; x2: number; y2: number }[], minX: number, minY: number, maxX: number, maxY: number): string {
        const w = Math.max(1, maxX - minX), h = Math.max(1, maxY - minY);
        const ph = h; // SVG y-aşağı: y'yi ters çevir
        const lines = segmentler.map(seg =>
            `<line x1="${(seg.x1 - minX).toFixed(3)}" y1="${(ph - (seg.y1 - minY)).toFixed(3)}" x2="${(seg.x2 - minX).toFixed(3)}" y2="${(ph - (seg.y2 - minY)).toFixed(3)}"/>`
        ).join('\n');
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w.toFixed(3)} ${h.toFixed(3)}" width="${w.toFixed(0)}mm" height="${h.toFixed(0)}mm">\n` +
            `<g stroke="#000" stroke-width="${(Math.max(w, h) / 500).toFixed(3)}" fill="none">\n${lines}\n</g>\n</svg>`;
    }

    /**
     * Cap (kapak) dolgu görünürlüğünü toggle et
     * Kesilen yüzeyin içini doldurur
     */
    public capGorunurlukToggle(): void {
        this.ayarlar.gosterKesitDolgu = !this.ayarlar.gosterKesitDolgu;

        // Eğer kesit aktifse, section fill'i güncelle
        if (this.aktif) {
            this.clippingPlanleriGuncelle();
        }

        this.sahne?.anaGosterim?.ciz();
        this.ayarlariKaydet();
    }

    /**
     * Cap rengini değiştir
     */
    public capRenginiDegistir(renk: number): void {
        this.ayarlar.kesitDolguRenk = renk;

        // Tüm cap mesh'lerinin rengini güncelle (contour triangulation dahil)
        this.sectionFillGroups.forEach((group) => {
            group.traverse((child: any) => {
                if (child.name === 'section_cap' ||
                    child.name === 'section_cap_group' ||
                    child.name === 'stencil_cap_group' ||
                    child.name === 'cap_contour_group' ||
                    child.name === 'cap_triangulation_group' ||
                    child.name === 'cap_backface_group' ||
                    child.name.startsWith('section_cap') ||
                    child.userData?.isSectionCap ||
                    child.userData?.isSectionCapBackface) {
                    if (child.material && child.material.color) {
                        child.material.color.setHex(renk);
                        child.material.needsUpdate = true;
                    }
                }
            });
        });

        this.sahne?.anaGosterim?.ciz();
        this.ayarlariKaydet();
    }

    /**
     * Cap opaklığını değiştir
     */
    public capOpakliginiDegistir(opacity: number): void {
        this.ayarlar.kesitDolguOpacity = Math.max(0, Math.min(1, opacity));

        // Tüm cap mesh'lerinin opaklığını güncelle (contour triangulation dahil)
        this.sectionFillGroups.forEach((group) => {
            group.traverse((child: any) => {
                if (child.name === 'section_cap' ||
                    child.name === 'section_cap_group' ||
                    child.name === 'stencil_cap_group' ||
                    child.name === 'cap_contour_group' ||
                    child.name === 'cap_triangulation_group' ||
                    child.name === 'cap_backface_group' ||
                    child.name.startsWith('section_cap') ||
                    child.userData?.isSectionCap ||
                    child.userData?.isSectionCapBackface) {
                    if (child.material) {
                        child.material.opacity = this.ayarlar.kesitDolguOpacity;
                        child.material.needsUpdate = true;
                    }
                }
            });
        });

        this.sahne?.anaGosterim?.ciz();
        this.ayarlariKaydet();
    }

    /**
     * Taşıma oklarının görünürlüğünü toggle et
     */
    public okGorunurlukToggle(): void {
        this.okGorunur = !this.okGorunur;
        this.manipulatorler.forEach(m => {
            if (m.oklar) {
                m.oklar.visible = this.okGorunur;
                // 🎯 Raycast'i de kapat/aç
                m.oklar.traverse((child: any) => {
                    if (child.isMesh || child.isLine) {
                        if (!this.okGorunur) {
                            if (!child._originalRaycast) {
                                child._originalRaycast = child.raycast;
                            }
                            child.raycast = () => {};
                        } else if (child._originalRaycast) {
                            child.raycast = child._originalRaycast;
                        }
                    }
                });
            }
        });
        this.sahne?.anaGosterim?.ciz();
        this.ayarlariKaydet();
    }

    /**
     * Eksen yardımcısının görünürlüğünü toggle et
     */
    public eksenGorunurlukToggle(): void {
        this.eksenGorunur = !this.eksenGorunur;
        this.manipulatorler.forEach(m => {
            if (m.eksenHelper) {
                m.eksenHelper.visible = this.eksenGorunur;
                // 🎯 Raycast'i de kapat/aç
                if (!this.eksenGorunur) {
                    if (!m.eksenHelper.userData._originalRaycast) {
                        m.eksenHelper.userData._originalRaycast = m.eksenHelper.raycast.bind(m.eksenHelper);
                    }
                    m.eksenHelper.raycast = () => {};
                } else if (m.eksenHelper.userData._originalRaycast) {
                    m.eksenHelper.raycast = m.eksenHelper.userData._originalRaycast;
                }
            }
        });
        this.sahne?.anaGosterim?.ciz();
        this.ayarlariKaydet();
    }

    /**
     * Ayarları güncelle - Shader uniform'larını update et
     */
    public ayarlariGuncelle(yeniAyarlar: Partial<FikoDinamikKesitAyarlar>): void {
        this.ayarlar = { ...this.ayarlar, ...yeniAyarlar };

        // Manipülatörleri güncelle
        this.manipulatorler.forEach(m => m.ayarlariGuncelle(this.ayarlar));

        // Shader uniform'larını güncelle (otomatik olarak sonraki frame'de uygulanacak)
        if (this.aktif) {
            this.clippingPlanleriGuncelle();

            if (this.sahne?.anaGosterim) {
                this.sahne.anaGosterim.ciz();
            }
        }

        // 🎯 Ayarları localStorage'a kaydet
        this.ayarlariKaydet();
    }

    /**
     * 🎯 Ayarları localStorage'dan yükle
     */
    private ayarlariYukle(): void {
        try {
            const kayitliAyarlar = localStorage.getItem(KESIT_AYARLAR_KEY);
            if (kayitliAyarlar) {
                const parsed = JSON.parse(kayitliAyarlar);

                // Varsayılan ayarlarla birleştir (eksik ayarlar için)
                this.ayarlar = { ...this.ayarlar, ...parsed };

                // Görünürlük flag'lerini de yükle
                if (parsed.halkaGorunur !== undefined) this.halkaGorunur = parsed.halkaGorunur;
                if (parsed.gridGorunur !== undefined) this.gridGorunur = parsed.gridGorunur;
                if (parsed.kesitCizgisiGorunur !== undefined) this.kesitCizgisiGorunur = parsed.kesitCizgisiGorunur;
                if (parsed.okGorunur !== undefined) this.okGorunur = parsed.okGorunur;
                if (parsed.eksenGorunur !== undefined) this.eksenGorunur = parsed.eksenGorunur;
            }
        } catch (e) {
            // Error loading settings - silently ignored
        }
    }

    /**
     * 🎯 Ayarları localStorage'a kaydet
     */
    private ayarlariKaydet(): void {
        try {
            // Görünürlük flag'lerini de ayarlara ekle
            const kayitAyarlar = {
                ...this.ayarlar,
                halkaGorunur: this.halkaGorunur,
                gridGorunur: this.gridGorunur,
                kesitCizgisiGorunur: this.kesitCizgisiGorunur,
                okGorunur: this.okGorunur,
                eksenGorunur: this.eksenGorunur
            };

            localStorage.setItem(KESIT_AYARLAR_KEY, JSON.stringify(kayitAyarlar));
        } catch (e) {
            // Error saving settings - silently ignored
        }
    }

    /**
     * Mouse interaction başlat
     */
    private mouseInteractionBaslat(): void {
        if (!this.sahne?.anaGosterim?.canvas) {
            return;
        }

        const camera = this.sahne.anaGosterim.aktifKamera;
        const canvas = this.sahne.anaGosterim.canvas.nativeElement;

        if (!camera || !canvas) {
            return;
        }

        this.interactionHandler = new FikoDinamikKesitInteraction(
            this,
            camera,
            canvas
        );

        this.interactionHandler.baslat();
    }

    /**
     * Mouse interaction durdur
     */
    private mouseInteractionDurdur(): void {
        if (this.interactionHandler) {
            this.interactionHandler.dispose();
            this.interactionHandler = null;
        }
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        this.kesitiDurdur();
        this.mouseInteractionDurdur();
        this.yuzeySecimDurdur();

        // Cleanup shader system
        FikoDinamikKesitShader.dispose();
        this.injectedMaterials.clear();
        this.sectionFillGroups.clear();

        this.manipulatorler.forEach(m => m.dispose());
        this.manipulatorler = [];

        this.kesitGeometrileriniTemizle();
    }

    override toJSON(): any {
        return {
            type: this.type,
            isim: this.name,
            matrix: this.matrix.toArray(),
            ayarlar: this.ayarlar,
            aktif: this.aktif,
            kesitDuzlemleri: this.kesitDuzlemleri.map(p => ({
                normal: p.normal.toArray(),
                constant: p.constant
            }))
        };
    }

    static fromJson(sahne?: FikoSahne, parent?: any, json?: any): FikoDinamikKesit {
        const kesit = new FikoDinamikKesit(sahne, json.isim);

        if (json.ayarlar) {
            kesit.ayarlar = json.ayarlar;
        }

        if (json.kesitDuzlemleri) {
            json.kesitDuzlemleri.forEach((p: any) => {
                const normal = new THREE.Vector3().fromArray(p.normal);
                kesit.yeniKesitDuzlemiEkle(normal, p.constant);
            });
        }

        kesit.parent = parent;
        return kesit;
    }
}

/**
 * Kesit Manipülatör (Gizatör)
 */
export class FikoKesitManipulator extends THREE.Group {
    // 🎯 PUBLIC: Görünürlük kontrolü için erişilebilir
    public duzlemMesh: THREE.Mesh;
    public gridHelper: THREE.GridHelper;
    public dondurmeHalkalar: THREE.Group;
    public oklar: THREE.Group;
    public eksenHelper: THREE.AxesHelper;

    private aktif: boolean = true;

    constructor(
        public plane: THREE.Plane,
        public ayarlar: FikoDinamikKesitAyarlar,
        public index: number,
        public parent: FikoDinamikKesit
    ) {
        super();

        this.name = `Kesit Manipülatör ${index + 1}`;
        this.userData.kesitManipulator = true;

        this.duzlemMeshOlustur();
        this.oklariOlustur();
        this.dondurmeHalkalariniOlustur();
        this.eksenleriniOlustur();

        this.guncelle();
    }

    /**
     * Düzlem mesh'i oluştur
     */
    private duzlemMeshOlustur(): void {
        // Başlangıç boyutunu küçük tut, dinamikBoyutAyarla() büyütecek
        const baslangicBoyutu = 50;

        const geometry = new THREE.PlaneGeometry(baslangicBoyutu, baslangicBoyutu);

        const material = new THREE.MeshBasicMaterial({
            color: 0x00ff00,
            transparent: true,
            opacity: this.ayarlar.duzlemOpacity,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });

        this.duzlemMesh = new THREE.Mesh(geometry, material);
        this.duzlemMesh.renderOrder = 998;
        this.duzlemMesh.name = 'section_plane_mesh';
        this.duzlemMesh.userData.isPlaneMesh = true;
        this.add(this.duzlemMesh);

        // Grid helper
        this.gridHelper = new THREE.GridHelper(
            baslangicBoyutu,
            20,
            0x00ff00,
            0x00ff00
        );
        this.gridHelper.rotation.x = Math.PI / 2;
        this.gridHelper.name = 'section_plane_grid';
        this.gridHelper.userData.isPlaneMesh = true;
        (this.gridHelper.material as THREE.Material).transparent = true;
        (this.gridHelper.material as THREE.Material).opacity = 0.3;
        (this.gridHelper.material as THREE.Material).depthTest = false;
        this.duzlemMesh.add(this.gridHelper);
    }

    /**
     * Okları oluştur (Translation)
     */
    private oklariOlustur(): void {
        this.oklar = new THREE.Group();
        this.oklar.name = 'Translation Arrows';
        this.oklar.userData.kesitManipulator = true; // 🎯 Seçim filtrelemesi için

        const okBoyutu = this.ayarlar.duzlemBoyutu * 0.3;

        // Z ekseni ok (normal yönünde)
        const okGeometry = new THREE.ConeGeometry(okBoyutu * 0.1, okBoyutu * 0.3, 8);
        const okMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000, depthTest: false });
        const ok = new THREE.Mesh(okGeometry, okMaterial);
        ok.position.z = okBoyutu * 0.5;
        ok.renderOrder = 999;
        ok.name = 'arrow_head';
        ok.userData.isArrow = true;
        ok.userData.kesitManipulator = true; // 🎯 Seçim filtrelemesi için

        const sapGeometry = new THREE.CylinderGeometry(okBoyutu * 0.03, okBoyutu * 0.03, okBoyutu * 0.7, 8);
        const sap = new THREE.Mesh(sapGeometry, okMaterial);
        sap.rotation.x = Math.PI / 2;
        sap.position.z = okBoyutu * 0.15;
        sap.renderOrder = 999;
        sap.name = 'arrow_shaft';
        sap.userData.isArrow = true;
        sap.userData.kesitManipulator = true; // 🎯 Seçim filtrelemesi için

        this.oklar.add(ok);
        this.oklar.add(sap);

        this.add(this.oklar);
    }

    /**
     * Dönme halkalarını oluştur (Rotation)
     */
    private dondurmeHalkalariniOlustur(): void {
        this.dondurmeHalkalar = new THREE.Group();
        this.dondurmeHalkalar.name = 'Rotation Rings';
        this.dondurmeHalkalar.userData.kesitManipulator = true; // 🎯 Seçim filtrelemesi için

        const radius = this.ayarlar.duzlemBoyutu * 0.5;

        // X ekseni halkası (kırmızı)
        const xRing = new THREE.RingGeometry(radius * 0.95, radius, 64);
        const xMaterial = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.3,
            depthTest: false
        });
        const xMesh = new THREE.Mesh(xRing, xMaterial);
        xMesh.rotation.y = Math.PI / 2;
        xMesh.renderOrder = 999;
        xMesh.name = 'rotation_ring_x';
        xMesh.userData.isRotationRing = true;
        xMesh.userData.kesitManipulator = true; // 🎯 Seçim filtrelemesi için
        this.dondurmeHalkalar.add(xMesh);

        // Y ekseni halkası (yeşil)
        const yRing = new THREE.RingGeometry(radius * 0.95, radius, 64);
        const yMaterial = new THREE.MeshBasicMaterial({
            color: 0x00ff00,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.3,
            depthTest: false
        });
        const yMesh = new THREE.Mesh(yRing, yMaterial);
        yMesh.rotation.x = Math.PI / 2;
        yMesh.renderOrder = 999;
        yMesh.name = 'rotation_ring_y';
        yMesh.userData.isRotationRing = true;
        yMesh.userData.kesitManipulator = true; // 🎯 Seçim filtrelemesi için
        this.dondurmeHalkalar.add(yMesh);

        this.add(this.dondurmeHalkalar);
    }

    /**
     * Eksenleri oluştur (Axes)
     */
    private eksenleriniOlustur(): void {
        const eksenBoyutu = 100;
        this.eksenHelper = new THREE.AxesHelper(eksenBoyutu);
        this.eksenHelper.name = 'axes_helper';
        this.eksenHelper.renderOrder = 999;
        this.eksenHelper.userData.kesitManipulator = true; // 🎯 Seçim filtrelemesi için
        this.add(this.eksenHelper);
    }

    /**
     * Manipülatörü güncelle
     */
    public guncelle(): void {
        // Kesit çizgilerinin merkezini hesapla
        let manipulatorMerkezi: THREE.Vector3 | null = null;

        const kesitCizgileri: THREE.LineSegments[] = [];
        if (this.parent?.kesitGeometrileri) {
            this.parent.kesitGeometrileri.traverse((child: any) => {
                if (child instanceof THREE.LineSegments) {
                    kesitCizgileri.push(child);
                }
            });
        }

        if (kesitCizgileri.length > 0) {
            // Kesit çizgilerinin merkezini hesapla
            const kesitNoktalar: THREE.Vector3[] = [];

            kesitCizgileri.forEach((cizgi: THREE.LineSegments) => {
                const geometry = cizgi.geometry as THREE.BufferGeometry;
                if (!geometry || !geometry.attributes.position) return;

                const positions = geometry.attributes.position;
                for (let i = 0; i < positions.count; i++) {
                    const nokta = new THREE.Vector3(
                        positions.getX(i),
                        positions.getY(i),
                        positions.getZ(i)
                    );
                    nokta.applyMatrix4(cizgi.matrixWorld);
                    kesitNoktalar.push(nokta);
                }
            });

            if (kesitNoktalar.length > 0) {
                const kesitBBox = new THREE.Box3();
                kesitNoktalar.forEach(nokta => kesitBBox.expandByPoint(nokta));

                manipulatorMerkezi = new THREE.Vector3();
                kesitBBox.getCenter(manipulatorMerkezi);
            }
        }

        // Manipulator pozisyonunu ayarla (kesit merkezi varsa onu kullan, yoksa plane'den hesapla)
        if (manipulatorMerkezi) {
            this.position.copy(manipulatorMerkezi);
        } else {
            const center = this.plane.normal.clone().multiplyScalar(-this.plane.constant);
            this.position.copy(center);
        }

        // Düzlemin normal'ine göre rotasyon ayarla
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), this.plane.normal);
        this.quaternion.copy(quaternion);

        // Grid ve düzlem boyutunu dinamik olarak ayarla
        this.dinamikBoyutAyarla();
    }

    /**
     * Grid boyutunu ve pozisyonunu kesit çizgilerine göre dinamik ayarla
     */
    public dinamikBoyutAyarla(): void {
        // Kesit geometrilerinden LineSegments'leri topla
        const kesitCizgileri: THREE.LineSegments[] = [];

        if (this.parent?.kesitGeometrileri) {
            this.parent.kesitGeometrileri.traverse((child: any) => {
                if (child instanceof THREE.LineSegments) {
                    kesitCizgileri.push(child);
                }
            });
        }

        // Kesit çizgilerini kontrol et
        if (kesitCizgileri.length === 0) {
            // Kesit çizgisi yoksa varsayılan boyut kullan
            if (this.duzlemMesh) {
                this.duzlemMesh.position.set(0, 0, 0);
                this.duzlemMesh.scale.set(1, 1, 1);
            }
            return;
        }

        // Kesit çizgilerinin tüm noktalarını topla
        const kesitNoktalar: THREE.Vector3[] = [];

        kesitCizgileri.forEach((cizgi: THREE.LineSegments) => {
            const geometry = cizgi.geometry as THREE.BufferGeometry;
            if (!geometry || !geometry.attributes.position) return;

            const positions = geometry.attributes.position;

            for (let i = 0; i < positions.count; i++) {
                const nokta = new THREE.Vector3(
                    positions.getX(i),
                    positions.getY(i),
                    positions.getZ(i)
                );
                // World space'e çevir
                nokta.applyMatrix4(cizgi.matrixWorld);
                kesitNoktalar.push(nokta);
            }
        });

        if (kesitNoktalar.length === 0) {
            return;
        }

        // Kesit noktalarının bounding box'ını hesapla
        const kesitBBox = new THREE.Box3();
        kesitNoktalar.forEach(nokta => {
            kesitBBox.expandByPoint(nokta);
        });

        // Kesit merkezini hesapla
        const kesitMerkezi = new THREE.Vector3();
        kesitBBox.getCenter(kesitMerkezi);

        // Kesit boyutunu hesapla
        const kesitBoyut = new THREE.Vector3();
        kesitBBox.getSize(kesitBoyut);
        const maxKesitBoyut = Math.max(kesitBoyut.x, kesitBoyut.y, kesitBoyut.z);

        // Grid boyutunu kesit boyutuna göre ayarla (çok daha büyük)
        const baslangicBoyutu = 50;
        const yeniBoyut = Math.max(maxKesitBoyut * 2.5, baslangicBoyutu);
        const maxBoyut = baslangicBoyutu * 10;
        const sinirliBoyut = Math.min(yeniBoyut, maxBoyut);
        const scaleFactor = sinirliBoyut / baslangicBoyutu;

        // Grid pozisyonunu sıfırla (manipulator zaten doğru konumda)
        // Sadece boyutunu ayarla
        if (this.duzlemMesh) {
            this.duzlemMesh.position.set(0, 0, 0); // Manipulator'a göre merkez
            this.duzlemMesh.scale.set(scaleFactor, scaleFactor, 1);
        }

        // Grid helper ve eksen'i de ölçeklendir
        if (this.gridHelper) {
            this.gridHelper.scale.set(scaleFactor, scaleFactor, scaleFactor);
        }

        if (this.eksenHelper) {
            this.eksenHelper.scale.set(scaleFactor * 0.5, scaleFactor * 0.5, scaleFactor * 0.5);
        }
    }

    /**
     * Aktif/Pasif durumu ayarla
     */
    public setAktif(aktif: boolean): void {
        this.aktif = aktif;

        if (this.duzlemMesh.material) {
            (this.duzlemMesh.material as THREE.MeshBasicMaterial).opacity = aktif
                ? this.ayarlar.duzlemOpacity
                : this.ayarlar.duzlemOpacity * 0.3;
        }

        this.oklar.visible = aktif;
        this.dondurmeHalkalar.visible = aktif;
    }

    /**
     * Ayarları güncelle
     */
    public ayarlariGuncelle(ayarlar: FikoDinamikKesitAyarlar): void {
        this.ayarlar = ayarlar;

        if (this.duzlemMesh.material) {
            (this.duzlemMesh.material as THREE.MeshBasicMaterial).opacity = this.aktif
                ? ayarlar.duzlemOpacity
                : ayarlar.duzlemOpacity * 0.3;
        }

        this.visible = ayarlar.gosterKesitDuzlemi;
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        this.traverse((child) => {
            if ((child as THREE.Mesh).geometry) {
                (child as THREE.Mesh).geometry.dispose();
            }
            if ((child as THREE.Mesh).material) {
                const mat = (child as THREE.Mesh).material;
                if (Array.isArray(mat)) {
                    mat.forEach(m => m.dispose());
                } else {
                    mat.dispose();
                }
            }
        });
    }
}

/**
 * Fiko Dinamik Kesit Ayarları
 */
export interface FikoDinamikKesitAyarlar {
    kesitModu: 'smooth' | 'flip';
    kesitDolguRenk: number;
    kesitDolguOpacity: number;
    kesitCizgiRenk: number;
    kesitCizgiKalinlik: number;
    gosterKesitDuzlemi: boolean;
    gosterKesitDolgu: boolean;
    gosterKesitCizgi: boolean;
    duzlemBoyutu: number;
    duzlemOpacity: number;
    // Görünürlük toggle'ları
    halkaGorunur?: boolean;
    gridGorunur?: boolean;
    kesitCizgisiGorunur?: boolean;
    okGorunur?: boolean;
    eksenGorunur?: boolean;
}

// localStorage key
const KESIT_AYARLAR_KEY = 'fikoyt_dinamik_kesit_ayarlar';
